import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { usePermissions } from "../contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, Package, Upload, Search, Plus,
  Printer, Mail, ShoppingCart, X, Trash2, Edit2, Check, AlertTriangle,
  Eye, EyeOff, Columns, ChevronDown, Filter, ArrowUpDown, ArrowUp, ArrowDown,
  Calendar, Clock, Warehouse
} from "lucide-react";
import { useIsArchived, ArchivedBanner } from "../components/ArchivedBanner";

// Custom bekræftelses-dialog (erstatter window.confirm som ikke virker i preview)
function ConfirmDialog({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-card border border-border rounded-t-xl sm:rounded-lg p-6 w-full sm:max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Annuller</Button>
          <Button variant="destructive" onClick={onConfirm}>Slet</Button>
        </div>
      </div>
    </div>
  );
}

// Kolonne-definitioner - matcher Excel-import
const ALL_COLUMNS = [
  { id: 'status', label: 'Status', default: false, filterable: true, sortable: true, adminOnly: true },
  { id: 'bestiller', label: 'Bestiller', default: false, filterable: true, sortable: true },
  { id: 'omraade', label: 'Område', default: true, filterable: true, sortable: true },
  { id: 'kategori', label: 'Kategori', default: true, filterable: true, sortable: true },
  { id: 'produktinformation', label: 'Produkt', default: true, sortable: true },
  { id: 'leverandoer', label: 'Leverandør', default: true, filterable: true, sortable: true },
  { id: 'produktnr', label: 'Produktnr.', default: false, filterable: true, sortable: true },
  { id: 'enhed', label: 'Enhed', default: false, filterable: true, sortable: true },
  { id: 'antal', label: 'Antal', default: true, sortable: true },
  { id: 'brugsdatoer', label: 'Brugsdatoer', default: true, sortable: false }, // NYT: Flere brugsdatoer
  { id: 'leveringsdato', label: 'Leveringsdato', default: false, sortable: true, adminOnly: true }, // Admin-only
  { id: 'lev_dato', label: 'Lev. dato (gammel)', default: false, sortable: true, adminOnly: true },
  { id: 'afh_dato', label: 'Afh. dato', default: false, sortable: true },
  { id: 'antal_dage', label: 'Antal dage', default: false, sortable: true },
  { id: 'antal_uger', label: 'Antal uger', default: false, sortable: true },
  { id: 'pris', label: 'Pris', default: true, sortable: true, adminOnly: true },
  { id: 'budgetkode', label: 'Budgetkode', default: false, filterable: true, sortable: true },
  { id: 'bemærkninger', label: 'Bemærkninger', default: false, adminOnly: true }, // Admin skriver, andre læser
  { id: 'kommentar', label: 'Kommentar', default: false },
  { id: 'deadline_bestilling', label: 'Deadline bestilling', default: false, sortable: true },
  { id: 'deadline_endelig', label: 'Endelig deadline', default: false, sortable: true },
];

// Felter som almindelige brugere kan redigere
const USER_EDITABLE_FIELDS = ['bestiller', 'omraade', 'enhed', 'antal', 'brugsdatoer', 'kommentar', 'budgetkode'];

const EMPTY_ITEM = {
  enhed: '', bemærkninger: '', leverandoer: '', kategori: '',
  produktinformation: '', produktnr: '', antal: '', lev_dato: '',
  afh_dato: '', antal_dage: '', pris: '', brugsdatoer: [], leveringsdato: ''
};

// Komponent til at håndtere flere brugsdatoer
function BrugsdatoerEditor({ dates = [], onChange, disabled = false }) {
  const parsedDates = Array.isArray(dates) ? dates : (dates ? [dates] : []);
  
  const addDate = () => {
    onChange([...parsedDates, '']);
  };
  
  const removeDate = (index) => {
    onChange(parsedDates.filter((_, i) => i !== index));
  };
  
  const updateDate = (index, value) => {
    const newDates = [...parsedDates];
    newDates[index] = value;
    onChange(newDates);
  };
  
  // Beregn antal dage (fra første til sidste dato)
  const calculateDays = () => {
    const validDates = parsedDates.filter(d => d).map(d => new Date(d)).sort((a, b) => a - b);
    if (validDates.length < 2) return validDates.length;
    const diffTime = validDates[validDates.length - 1] - validDates[0];
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };
  
  if (disabled) {
    return (
      <div className="flex flex-wrap gap-1">
        {parsedDates.length === 0 ? (
          <span className="text-muted-foreground">-</span>
        ) : parsedDates.map((d, i) => (
          <span key={i} className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
            {d ? new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) : '-'}
          </span>
        ))}
        {parsedDates.length > 1 && (
          <span className="text-xs text-muted-foreground">({calculateDays()} dage)</span>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      {parsedDates.map((date, index) => (
        <div key={index} className="flex items-center gap-1">
          <Input
            type="date"
            value={date || ''}
            onChange={(e) => updateDate(index, e.target.value)}
            className="h-7 text-xs w-32"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => removeDate(index)}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={addDate}
        className="h-6 text-xs"
      >
        <Plus className="h-3 w-3 mr-1" />Tilføj dato
      </Button>
      {parsedDates.length > 1 && (
        <div className="text-xs text-muted-foreground">
          Periode: {calculateDays()} dage
        </div>
      )}
    </div>
  );
}

// Beregn antal dage fra brugsdatoer
function calculateDaysFromDates(dates) {
  const parsedDates = Array.isArray(dates) ? dates : (dates ? [dates] : []);
  const validDates = parsedDates.filter(d => d).map(d => new Date(d)).sort((a, b) => a - b);
  if (validDates.length < 2) return validDates.length;
  const diffTime = validDates[validDates.length - 1] - validDates[0];
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

export default function Materiel({ user }) {
  const navigate = useNavigate();
  const isArchived = useIsArchived();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]); // Bestillinger for valgt år
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState("basisliste"); // "basisliste" eller "bestillinger"
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.filter(c => c.default).map(c => c.id)
  );
  // Filtre
  const [filterOmraade, setFilterOmraade] = useState('');
  const [filterKategori, setFilterKategori] = useState('');
  const [filterLeverandoer, setFilterLeverandoer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBestiller, setFilterBestiller] = useState('');
  const [filterEnhed, setFilterEnhed] = useState('');
  const [filterBudgetkode, setFilterBudgetkode] = useState('');
  const [filterDatoFra, setFilterDatoFra] = useState('');
  const [filterDatoTil, setFilterDatoTil] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Sortering
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' eller 'desc'
  // Skjulte linjer
  const [hiddenItemIds, setHiddenItemIds] = useState(() => {
    const saved = localStorage.getItem(`hiddenMateriel_${localStorage.getItem("selectedEventId")}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  // Antal-popover
  const [quantityPopover, setQuantityPopover] = useState({ open: false, itemId: null, quantity: 1 });
  // Kurv kolonner
  const [cartVisibleColumns, setCartVisibleColumns] = useState(['produktinformation', 'antal', 'leverandoer', 'omraade', 'lev_dato']);
  const [showCartColumnPicker, setShowCartColumnPicker] = useState(false);
  
  const fileInputRef = useRef(null);
  const columnPickerRef = useRef(null);
  const cartColumnPickerRef = useRef(null);
  
  const token = localStorage.getItem("token");
  const eventId = localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName");
  const isAdmin = user?.role === 'admin';
  const isAdminAssistant = user?.role === 'admin_assistant';
  const canEditAllFields = isAdmin || isAdminAssistant; // Admin og bestiller-admin kan ændre alt
  
  // Hent rettigheder fra context
  const { canPerformAction, hasPermission } = usePermissions();
  const canEditMateriel = isAdmin || hasPermission('materiel_rediger');
  const canDeleteMateriel = isAdmin || hasPermission('materiel_slet');
  const canImportMateriel = isAdmin || hasPermission('materiel_import');
  
  // Hjælpefunktion: Kan bruger redigere dette felt?
  const canEditField = (fieldId) => {
    if (canEditAllFields) return true;
    return USER_EDITABLE_FIELDS.includes(fieldId);
  };

  const availableYears = [2026, 2025, 2024];

  // Luk kolonne-vælger ved klik udenfor
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadData();
  }, [eventId, selectedYear, viewMode]);

  const loadData = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      if (viewMode === "basisliste") {
        const res = await axios.get(`${API}/events/${eventId}/materiel`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(res.data);
      } else {
        // Hent bestillinger for valgt år
        const res = await axios.get(`${API}/events/${eventId}/materiel/orders?year=${selectedYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(res.data);
      }
    } catch (error) {
      console.error("Fejl:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/events/${eventId}/materiel/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved upload");
    }
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  const deleteAllMateriel = () => {
    showConfirm(
      "Slet alt materiel?",
      "Er du sikker på du vil slette AL materiel-data? Denne handling kan ikke fortrydes.",
      async () => {
        try {
          const res = await axios.delete(`${API}/events/${eventId}/materiel`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(res.data.message);
          closeConfirm();
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
          closeConfirm();
        }
      }
    );
  };

  const deleteItem = (itemId, produktnavn) => {
    showConfirm(
      "Slet linje?",
      `Er du sikker på du vil slette "${produktnavn}"?`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/materiel/${itemId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Linje slettet");
          closeConfirm();
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
          closeConfirm();
        }
      }
    );
  };

  const saveItem = async (item) => {
    try {
      await axios.put(`${API}/events/${eventId}/materiel/${item.id}`, item, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ændringer gemt");
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gem");
    }
  };

  const addNewItem = async () => {
    if (!newItem.produktinformation) {
      toast.error("Produktinformation er påkrævet");
      return;
    }
    try {
      await axios.post(`${API}/events/${eventId}/materiel`, { ...newItem, event_id: eventId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ny linje tilføjet");
      setShowAddForm(false);
      setNewItem(EMPTY_ITEM);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved tilføjelse");
    }
  };

  const addToCart = (item, quantity = 1) => {
    const existingIndex = cart.findIndex(c => c.id === item.id);
    if (existingIndex >= 0) {
      // Opdater antal hvis allerede i kurv
      const newCart = [...cart];
      newCart[existingIndex].bestilAntal += quantity;
      setCart(newCart);
      toast.success(`Opdateret antal til ${newCart[existingIndex].bestilAntal}`);
    } else {
      setCart([...cart, { ...item, bestilAntal: quantity }]);
      toast.success(`${quantity}x tilføjet til kurv`);
    }
    setQuantityPopover({ open: false, itemId: null, quantity: 1 });
  };

  const removeFromCart = (itemId) => setCart(cart.filter(c => c.id !== itemId));
  const updateCartQuantity = (itemId, antal) => setCart(cart.map(c => c.id === itemId ? { ...c, bestilAntal: antal } : c));
  const updateCartField = (itemId, field, value) => setCart(cart.map(c => c.id === itemId ? { ...c, [field]: value } : c));

  // Skjul/vis linjer
  const hideItem = (itemId) => {
    const newHidden = [...hiddenItemIds, itemId];
    setHiddenItemIds(newHidden);
    localStorage.setItem(`hiddenMateriel_${eventId}`, JSON.stringify(newHidden));
    toast.success("Linje skjult");
  };

  const unhideItem = (itemId) => {
    const newHidden = hiddenItemIds.filter(id => id !== itemId);
    setHiddenItemIds(newHidden);
    localStorage.setItem(`hiddenMateriel_${eventId}`, JSON.stringify(newHidden));
    toast.success("Linje vist igen");
  };

  const unhideAllItems = () => {
    setHiddenItemIds([]);
    localStorage.removeItem(`hiddenMateriel_${eventId}`);
    toast.success("Alle linjer vist");
  };

  const saveOrder = async () => {
    if (cart.length === 0) {
      toast.error("Kurven er tom");
      return;
    }
    try {
      await axios.post(`${API}/events/${eventId}/materiel/orders`, {
        year: selectedYear,
        items: cart.map(c => ({
          ...c,
          antal: c.bestilAntal,
          ordered_at: new Date().toISOString()
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bestilling gemt for ${selectedYear}`);
      setCart([]);
      setShowCart(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gem af bestilling");
    }
  };

  const printOrder = () => {
    // Byg header baseret på valgte kolonner
    const headers = cartVisibleColumns.map(colId => {
      const col = ALL_COLUMNS.find(c => c.id === colId);
      return col ? col.label : colId;
    });
    
    // Byg rækker baseret på valgte kolonner
    const rows = cart.map(item => {
      return cartVisibleColumns.map(colId => {
        if (colId === 'antal') return item.bestilAntal;
        return item[colId] || '-';
      });
    });
    
    const content = `<html><head><title>Bestilling ${eventName}</title><style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f5f5f5}</style></head><body><h1>Bestilling - ${eventName}</h1><p>Dato: ${new Date().toLocaleDateString('da-DK')}</p><table><tr>${headers.map(h=>`<th>${h}</th>`).join('')}</tr>${rows.map(row=>`<tr>${row.map(cell=>`<td>${cell}</td>`).join('')}</tr>`).join('')}</table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(content);
    w.document.close();
    w.print();
  };

  const sendEmail = () => {
    const subject = encodeURIComponent(`Bestilling - ${eventName}`);
    const body = encodeURIComponent(cart.map(i => `${i.produktinformation}: ${i.bestilAntal} stk`).join('\n'));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const displayItems = viewMode === "basisliste" ? items : orders;
  
  // Adskil synlige og skjulte items
  const visibleItems = displayItems.filter(item => !hiddenItemIds.includes(item.id));
  const hiddenItems = displayItems.filter(item => hiddenItemIds.includes(item.id));
  
  // Unikke værdier for filtre (kun fra synlige items)
  const uniqueOmraader = [...new Set(visibleItems.map(i => i.omraade).filter(Boolean))].sort();
  const uniqueKategorier = [...new Set(visibleItems.map(i => i.kategori).filter(Boolean))].sort();
  const uniqueLeverandoerer = [...new Set(visibleItems.map(i => i.leverandoer).filter(Boolean))].sort();
  const uniqueStatus = [...new Set(visibleItems.map(i => i.status).filter(Boolean))].sort();
  const uniqueBestillere = [...new Set(visibleItems.map(i => i.bestiller).filter(Boolean))].sort();
  const uniqueEnheder = [...new Set(visibleItems.map(i => i.enhed).filter(Boolean))].sort();
  const uniqueBudgetkoder = [...new Set(visibleItems.map(i => i.budgetkode).filter(Boolean))].sort();
  
  // Tjek om der er aktive filtre
  const hasActiveFilters = filterOmraade || filterKategori || filterLeverandoer || 
    filterStatus || filterBestiller || filterEnhed || filterBudgetkode || 
    filterDatoFra || filterDatoTil || searchTerm;
  
  // Filtrer items (kun synlige items)
  const filteredItems = visibleItems.filter(item => {
    // Søgeterm filter
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const matchSearch = (item.produktinformation || '').toLowerCase().includes(s) ||
             (item.leverandoer || '').toLowerCase().includes(s) ||
             (item.omraade || '').toLowerCase().includes(s) ||
             (item.kategori || '').toLowerCase().includes(s) ||
             (item.produktnr || '').toLowerCase().includes(s);
      if (!matchSearch) return false;
    }
    // Kombinérbare filtre
    if (filterOmraade && item.omraade !== filterOmraade) return false;
    if (filterKategori && item.kategori !== filterKategori) return false;
    if (filterLeverandoer && item.leverandoer !== filterLeverandoer) return false;
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterBestiller && item.bestiller !== filterBestiller) return false;
    if (filterEnhed && item.enhed !== filterEnhed) return false;
    if (filterBudgetkode && item.budgetkode !== filterBudgetkode) return false;
    // Datoperiode filter
    if (filterDatoFra && item.lev_dato && item.lev_dato < filterDatoFra) return false;
    if (filterDatoTil && item.lev_dato && item.lev_dato > filterDatoTil) return false;
    return true;
  });
  
  // Sortér items
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!sortColumn) return 0;
    let aVal = a[sortColumn] || '';
    let bVal = b[sortColumn] || '';
    // Numerisk sortering for tal-felter
    if (['antal', 'pris', 'antal_dage', 'antal_uger'].includes(sortColumn)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Håndter kolonne-sortering
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };
  
  // Nulstil alle filtre
  const resetFilters = () => {
    setFilterOmraade('');
    setFilterKategori('');
    setFilterLeverandoer('');
    setFilterStatus('');
    setFilterBestiller('');
    setFilterEnhed('');
    setFilterBudgetkode('');
    setFilterDatoFra('');
    setFilterDatoTil('');
    setSearchTerm('');
    setSortColumn('');
  };

  const groupedItems = sortedItems.reduce((acc, item) => {
    const k = item.kategori || 'Uden kategori';
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});

  const EditableCell = ({ value, field, item, type = "text" }) => {
    // Tjek om bruger kan redigere dette felt
    const canEdit = canEditField(field);
    
    // Speciel håndtering af brugsdatoer
    if (field === 'brugsdatoer') {
      const isEditing = editingItem?.id === item.id;
      if (isEditing && canEdit) {
        return (
          <BrugsdatoerEditor
            dates={editingItem.brugsdatoer || []}
            onChange={(newDates) => setEditingItem({ ...editingItem, brugsdatoer: newDates })}
            disabled={false}
          />
        );
      }
      return <BrugsdatoerEditor dates={item.brugsdatoer || []} disabled={true} />;
    }
    
    if (editingItem?.id !== item.id) {
      // Visnings-mode: formatér pris korrekt
      if (field === 'pris' && item.pris) {
        return <span>{Number(item.pris).toLocaleString()} kr</span>;
      }
      // Vis låse-ikon for admin-only felter som ikke kan redigeres
      if (!canEdit && ALL_COLUMNS.find(c => c.id === field)?.adminOnly) {
        return <span className="text-muted-foreground">{value || '-'}</span>;
      }
      return <span>{value || '-'}</span>;
    }
    
    // Hvis bruger ikke kan redigere feltet, vis det som read-only
    if (!canEdit) {
      if (field === 'pris' && item.pris) {
        return <span className="text-muted-foreground">{Number(item.pris).toLocaleString()} kr</span>;
      }
      return <span className="text-muted-foreground">{value || '-'}</span>;
    }
    
    // Bestem input type baseret på felt
    let inputType = type;
    if (field === 'lev_dato' || field === 'afh_dato' || field === 'deadline_bestilling' || field === 'deadline_endelig' || field === 'leveringsdato') {
      inputType = 'date';
    } else if (field === 'pris' || field === 'antal' || field === 'antal_dage' || field === 'antal_uger') {
      inputType = 'number';
    }
    
    return (
      <Input 
        type={inputType} 
        value={editingItem[field] || ''} 
        onChange={(e) => setEditingItem({ ...editingItem, [field]: e.target.value })} 
        className="h-7 text-sm" 
      />
    );
  };

  // Sorterbar header-celle
  const SortableHeader = ({ columnId, label, align = 'left' }) => {
    const col = ALL_COLUMNS.find(c => c.id === columnId);
    if (!col?.sortable) return <th className={`px-3 py-2 text-${align}`}>{label}</th>;
    
    return (
      <th 
        className={`px-3 py-2 text-${align} cursor-pointer hover:bg-muted/30 select-none`}
        onClick={() => handleSort(columnId)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
          {label}
          {sortColumn === columnId ? (
            sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-30" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Bekræftelses-dialog */}
      <ConfirmDialog 
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />

      {/* Tilføj modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Tilføj ny linje</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs text-muted-foreground">Produkt *</label><Input value={newItem.produktinformation} onChange={(e) => setNewItem({...newItem, produktinformation: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Enhed</label><Input value={newItem.enhed} onChange={(e) => setNewItem({...newItem, enhed: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Leverandør</label><Input value={newItem.leverandoer} onChange={(e) => setNewItem({...newItem, leverandoer: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Kategori</label><Input value={newItem.kategori} onChange={(e) => setNewItem({...newItem, kategori: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Antal</label><Input type="number" value={newItem.antal} onChange={(e) => setNewItem({...newItem, antal: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Pris</label><Input type="number" value={newItem.pris} onChange={(e) => setNewItem({...newItem, pris: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Lev. dato</label><Input type="date" value={newItem.lev_dato} onChange={(e) => setNewItem({...newItem, lev_dato: e.target.value})} /></div>
              <div><label className="text-xs text-muted-foreground">Afh. dato</label><Input type="date" value={newItem.afh_dato} onChange={(e) => setNewItem({...newItem, afh_dato: e.target.value})} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuller</Button>
              <Button onClick={addNewItem}><Plus className="h-4 w-4 mr-2" />Tilføj</Button>
            </div>
          </div>
        </div>
      )}

      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <h1 className="text-xl font-bold font-heading uppercase">Materiel</h1>
              <p className="text-xs text-muted-foreground">{eventName}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Visnings-toggle */}
            <div className="flex border border-border rounded-md overflow-hidden">
              <button 
                onClick={() => setViewMode("basisliste")}
                className={`px-3 py-1.5 text-sm ${viewMode === "basisliste" ? "bg-primary text-primary-foreground" : "bg-background"}`}
              >
                Basisliste
              </button>
              <button 
                onClick={() => setViewMode("bestillinger")}
                className={`px-3 py-1.5 text-sm ${viewMode === "bestillinger" ? "bg-primary text-primary-foreground" : "bg-background"}`}
              >
                Bestillinger
              </button>
            </div>

            {viewMode === "bestillinger" && (
              <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="px-3 py-2 bg-background border border-border rounded-md text-sm">
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            
            <Button variant={showCart ? "default" : "outline"} size="sm" onClick={() => setShowCart(!showCart)} className="relative" disabled={isArchived}>
              <ShoppingCart className="h-4 w-4 mr-2" />Kurv
              {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">{cart.length}</span>}
            </Button>
            
            {/* Lager/Nedpakning knap */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/lager')}
              data-testid="lager-btn"
            >
              <Warehouse className="h-4 w-4 mr-2" />Lager
            </Button>
            
            {viewMode === "basisliste" && (
              <>
                {canEditMateriel && !isArchived && (
                  <Button onClick={() => setShowAddForm(true)} variant="outline" size="sm" data-testid="add-materiel-btn">
                    <Plus className="h-4 w-4 mr-2" />Tilføj
                  </Button>
                )}
                {canImportMateriel && !isArchived && (
                  <>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" data-testid="import-materiel-btn">
                      <Upload className="h-4 w-4 mr-2" />Import
                    </Button>
                  </>
                )}
                {canDeleteMateriel && items.length > 0 && (
                  <Button onClick={deleteAllMateriel} variant="outline" size="sm" className="text-destructive border-destructive/30" data-testid="delete-all-materiel-btn">
                    <Trash2 className="h-4 w-4 mr-2" />Slet alt
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Søgning og filtre - sticky under header */}
        <div className="container mx-auto px-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Søgefelt */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Søg i produkter, leverandører, områder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Primære filtre */}
            <select value={filterOmraade} onChange={(e) => setFilterOmraade(e.target.value)} className="h-9 px-3 rounded-md border border-border bg-background text-sm">
              <option value="">Alle områder</option>
              {uniqueOmraader.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            
            <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)} className="h-9 px-3 rounded-md border border-border bg-background text-sm">
              <option value="">Alle kategorier</option>
              {uniqueKategorier.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            
            <select value={filterLeverandoer} onChange={(e) => setFilterLeverandoer(e.target.value)} className="h-9 px-3 rounded-md border border-border bg-background text-sm">
              <option value="">Alle leverandører</option>
              {uniqueLeverandoerer.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            
            {/* Avancerede filtre toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={showAdvancedFilters ? 'bg-primary/10 border-primary' : ''}
            >
              <Filter className="h-4 w-4 mr-1" />
              Flere filtre
              <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Nulstil filtre */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                <X className="h-4 w-4 mr-1" />
                Nulstil ({sortedItems.length}/{displayItems.length})
              </Button>
            )}
            
            {/* Kolonne-vælger */}
            <div className="relative ml-auto" ref={columnPickerRef}>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowColumnPicker(!showColumnPicker)}
                data-testid="column-picker-btn"
              >
                <Columns className="h-4 w-4 mr-2" />
                Kolonner ({visibleColumns.length})
                <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showColumnPicker ? 'rotate-180' : ''}`} />
              </Button>
              
              {showColumnPicker && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50 p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-sm">Vælg synlige kolonner</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id))}
                    >
                      Nulstil
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {ALL_COLUMNS.map(col => (
                      <button
                        key={col.id}
                      onClick={() => {
                        if (visibleColumns.includes(col.id)) {
                          // Tillad ikke at fjerne alle kolonner
                          if (visibleColumns.length > 1) {
                            setVisibleColumns(visibleColumns.filter(c => c !== col.id));
                          }
                        } else {
                          setVisibleColumns([...visibleColumns, col.id]);
                        }
                      }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${
                        visibleColumns.includes(col.id) 
                          ? 'bg-primary/10 text-primary' 
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {visibleColumns.includes(col.id) ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                      {col.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {isArchived && <ArchivedBanner />}
        {/* Avancerede filtre (skjult som standard) */}
        {showAdvancedFilters && (
          <div className="mb-4 p-4 bg-card border border-border rounded-lg">
            <h3 className="text-sm font-medium mb-3">Avancerede filtre</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Område</label>
                <select value={filterOmraade} onChange={(e) => setFilterOmraade(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="">Alle</option>
                  {uniqueOmraader.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="">Alle</option>
                  {uniqueStatus.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Bestiller</label>
                <select value={filterBestiller} onChange={(e) => setFilterBestiller(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="">Alle</option>
                  {uniqueBestillere.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Enhed</label>
                <select value={filterEnhed} onChange={(e) => setFilterEnhed(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="">Alle</option>
                  {uniqueEnheder.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Budgetkode</label>
                <select value={filterBudgetkode} onChange={(e) => setFilterBudgetkode(e.target.value)} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="">Alle</option>
                  {uniqueBudgetkoder.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Dato fra</label>
                <Input type="date" value={filterDatoFra} onChange={(e) => setFilterDatoFra(e.target.value)} className="h-9" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Dato til</label>
                <Input type="date" value={filterDatoTil} onChange={(e) => setFilterDatoTil(e.target.value)} className="h-9" />
              </div>
            </div>
          </div>
        )}

        {/* Kurv */}
        {showCart && (
          <div className="mb-6 p-4 bg-card border border-primary/30 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold"><ShoppingCart className="h-5 w-5 inline mr-2" />Kurv ({cart.length})</h2>
              <div className="flex gap-2">
                {/* Kurv kolonne-vælger */}
                <div className="relative" ref={cartColumnPickerRef}>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setShowCartColumnPicker(!showCartColumnPicker)}
                    data-testid="cart-column-picker-btn"
                  >
                    <Columns className="h-4 w-4 mr-1" />
                    Kolonner
                  </Button>
                  {showCartColumnPicker && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-50 p-3">
                      <h4 className="font-medium text-sm mb-2">Vælg kolonner til kurv/print</h4>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {ALL_COLUMNS.map(col => (
                          <button
                            key={col.id}
                            onClick={() => {
                              if (cartVisibleColumns.includes(col.id)) {
                                if (cartVisibleColumns.length > 1) {
                                  setCartVisibleColumns(cartVisibleColumns.filter(c => c !== col.id));
                                }
                              } else {
                                setCartVisibleColumns([...cartVisibleColumns, col.id]);
                              }
                            }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left ${
                              cartVisibleColumns.includes(col.id) ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                            }`}
                          >
                            {cartVisibleColumns.includes(col.id) ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                            {col.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={printOrder} disabled={!cart.length}><Printer className="h-4 w-4 mr-1" />Print</Button>
                <Button size="sm" variant="outline" onClick={sendEmail} disabled={!cart.length}><Mail className="h-4 w-4 mr-1" />Mail</Button>
                <Button size="sm" onClick={saveOrder} disabled={!cart.length}><Check className="h-4 w-4 mr-1" />Gem bestilling</Button>
              </div>
            </div>
            {cart.length === 0 ? (
              <p className="text-muted-foreground text-sm">Kurven er tom. Tilføj produkter fra basislisten.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      {cartVisibleColumns.map(colId => {
                        const col = ALL_COLUMNS.find(c => c.id === colId);
                        return <th key={colId} className="px-3 py-2 text-left font-medium">{col?.label || colId}</th>;
                      })}
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cart.map(item => (
                      <tr key={item.id} className="hover:bg-accent/5">
                        {cartVisibleColumns.map(colId => (
                          <td key={colId} className="px-3 py-2">
                            {colId === 'antal' ? (
                              <Input 
                                type="number" 
                                min="1" 
                                value={item.bestilAntal} 
                                onChange={(e) => updateCartQuantity(item.id, parseInt(e.target.value) || 1)} 
                                className="w-20 h-7 text-sm" 
                              />
                            ) : colId === 'lev_dato' || colId === 'afh_dato' ? (
                              <Input 
                                type="date" 
                                value={item[colId] || ''} 
                                onChange={(e) => updateCartField(item.id, colId, e.target.value)} 
                                className="h-7 text-sm w-32" 
                              />
                            ) : colId === 'kommentar' || colId === 'bemærkninger' ? (
                              <Input 
                                type="text" 
                                value={item[colId] || ''} 
                                onChange={(e) => updateCartField(item.id, colId, e.target.value)} 
                                className="h-7 text-sm" 
                                placeholder="..."
                              />
                            ) : (
                              <span>{item[colId] || '-'}</span>
                            )}
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          <Button size="sm" variant="ghost" onClick={() => removeFromCart(item.id)} className="h-7 w-7 p-0">
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Indhold */}
        {loading ? (
          <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div></div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-bold mb-2">
              {viewMode === "basisliste" ? "Ingen materialer i basislisten" : `Ingen bestillinger for ${selectedYear}`}
            </h2>
            <p className="text-muted-foreground text-sm mb-4">
              {viewMode === "basisliste" ? "Upload en Excel-fil eller tilføj manuelt" : "Tilføj produkter til kurven og gem en bestilling"}
            </p>
            {isAdmin && viewMode === "basisliste" && !isArchived && (
              <div className="flex justify-center gap-2">
                <Button onClick={() => setShowAddForm(true)}><Plus className="h-4 w-4 mr-2" />Tilføj</Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}><Upload className="h-4 w-4 mr-2" />Import</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedItems).sort().map(([kategori, kategoriItems]) => (
              <div key={kategori} className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-muted/30 border-b border-border">
                  <h3 className="font-bold text-sm uppercase">{kategori} ({kategoriItems.length})</h3>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/20">
                    <tr>
                      {visibleColumns.includes('status') && <SortableHeader columnId="status" label="Status" />}
                      {visibleColumns.includes('bestiller') && <SortableHeader columnId="bestiller" label="Bestiller" />}
                      {visibleColumns.includes('omraade') && <SortableHeader columnId="omraade" label="Område" />}
                      {visibleColumns.includes('kategori') && <SortableHeader columnId="kategori" label="Kategori" />}
                      {visibleColumns.includes('produktinformation') && <SortableHeader columnId="produktinformation" label="Produkt" />}
                      {visibleColumns.includes('leverandoer') && <SortableHeader columnId="leverandoer" label="Leverandør" />}
                      {visibleColumns.includes('produktnr') && <SortableHeader columnId="produktnr" label="Produktnr." />}
                      {visibleColumns.includes('enhed') && <SortableHeader columnId="enhed" label="Enhed" />}
                      {visibleColumns.includes('antal') && <SortableHeader columnId="antal" label="Antal" align="center" />}
                      {visibleColumns.includes('brugsdatoer') && <th className="px-3 py-2 text-left">Brugsdatoer</th>}
                      {visibleColumns.includes('leveringsdato') && canEditAllFields && <SortableHeader columnId="leveringsdato" label="Leveringsdato" align="center" />}
                      {visibleColumns.includes('lev_dato') && <SortableHeader columnId="lev_dato" label="Lev. dato" align="center" />}
                      {visibleColumns.includes('afh_dato') && <SortableHeader columnId="afh_dato" label="Afh. dato" align="center" />}
                      {visibleColumns.includes('antal_dage') && <SortableHeader columnId="antal_dage" label="Dage" align="center" />}
                      {visibleColumns.includes('antal_uger') && <SortableHeader columnId="antal_uger" label="Uger" align="center" />}
                      {visibleColumns.includes('pris') && <SortableHeader columnId="pris" label="Pris" align="right" />}
                      {visibleColumns.includes('budgetkode') && <SortableHeader columnId="budgetkode" label="Budgetkode" />}
                      {visibleColumns.includes('bemærkninger') && <th className="px-3 py-2 text-left">Bemærkninger</th>}
                      {visibleColumns.includes('kommentar') && <th className="px-3 py-2 text-left">Kommentar</th>}
                      {visibleColumns.includes('deadline_bestilling') && <SortableHeader columnId="deadline_bestilling" label="Deadline" align="center" />}
                      {visibleColumns.includes('deadline_endelig') && <SortableHeader columnId="deadline_endelig" label="Endelig" align="center" />}
                      <th className="px-3 py-2 text-center w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {kategoriItems.map(item => (
                      <tr 
                        key={item.id} 
                        className={`
                          ${editingItem?.id === item.id ? 'bg-primary/5' : 'hover:bg-accent/5'}
                          ${item.fra_lager ? 'border-l-4 border-l-blue-500 bg-blue-500/5' : ''}
                        `}
                        title={item.fra_lager ? 'Importeret fra lager' : ''}
                      >
                        {visibleColumns.includes('status') && <td className="px-3 py-2"><EditableCell value={item.status} field="status" item={item} /></td>}
                        {visibleColumns.includes('bestiller') && <td className="px-3 py-2"><EditableCell value={item.bestiller} field="bestiller" item={item} /></td>}
                        {visibleColumns.includes('omraade') && <td className="px-3 py-2"><EditableCell value={item.omraade} field="omraade" item={item} /></td>}
                        {visibleColumns.includes('kategori') && <td className="px-3 py-2"><EditableCell value={item.kategori} field="kategori" item={item} /></td>}
                        {visibleColumns.includes('produktinformation') && <td className="px-3 py-2"><EditableCell value={item.produktinformation} field="produktinformation" item={item} /></td>}
                        {visibleColumns.includes('leverandoer') && <td className="px-3 py-2"><EditableCell value={item.leverandoer} field="leverandoer" item={item} /></td>}
                        {visibleColumns.includes('produktnr') && <td className="px-3 py-2"><EditableCell value={item.produktnr} field="produktnr" item={item} /></td>}
                        {visibleColumns.includes('enhed') && <td className="px-3 py-2"><EditableCell value={item.enhed} field="enhed" item={item} /></td>}
                        {visibleColumns.includes('antal') && <td className="px-3 py-2 text-center"><EditableCell value={item.antal} field="antal" item={item} type="number" /></td>}
                        {visibleColumns.includes('brugsdatoer') && <td className="px-3 py-2"><EditableCell value={item.brugsdatoer} field="brugsdatoer" item={item} /></td>}
                        {visibleColumns.includes('leveringsdato') && canEditAllFields && <td className="px-3 py-2 text-center"><EditableCell value={item.leveringsdato} field="leveringsdato" item={item} /></td>}
                        {visibleColumns.includes('lev_dato') && <td className="px-3 py-2 text-center"><EditableCell value={item.lev_dato} field="lev_dato" item={item} /></td>}
                        {visibleColumns.includes('afh_dato') && <td className="px-3 py-2 text-center"><EditableCell value={item.afh_dato} field="afh_dato" item={item} /></td>}
                        {visibleColumns.includes('antal_dage') && <td className="px-3 py-2 text-center"><EditableCell value={item.antal_dage} field="antal_dage" item={item} /></td>}
                        {visibleColumns.includes('antal_uger') && <td className="px-3 py-2 text-center"><EditableCell value={item.antal_uger} field="antal_uger" item={item} /></td>}
                        {visibleColumns.includes('pris') && <td className="px-3 py-2 text-right"><EditableCell value={item.pris} field="pris" item={item} /></td>}
                        {visibleColumns.includes('budgetkode') && <td className="px-3 py-2"><EditableCell value={item.budgetkode} field="budgetkode" item={item} /></td>}
                        {visibleColumns.includes('bemærkninger') && <td className="px-3 py-2 text-sm text-muted-foreground"><EditableCell value={item.bemærkninger} field="bemærkninger" item={item} /></td>}
                        {visibleColumns.includes('kommentar') && <td className="px-3 py-2 text-sm text-muted-foreground"><EditableCell value={item.kommentar} field="kommentar" item={item} /></td>}
                        {visibleColumns.includes('deadline_bestilling') && <td className="px-3 py-2 text-center"><EditableCell value={item.deadline_bestilling} field="deadline_bestilling" item={item} /></td>}
                        {visibleColumns.includes('deadline_endelig') && <td className="px-3 py-2 text-center"><EditableCell value={item.deadline_endelig} field="deadline_endelig" item={item} /></td>}
                        <td className="px-3 py-2">
                          <div className="flex justify-center gap-1">
                            {editingItem?.id === item.id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => saveItem(editingItem)} className="h-7 w-7 p-0 text-green-500" data-testid="save-materiel-btn"><Check className="h-4 w-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)} className="h-7 w-7 p-0" data-testid="cancel-edit-btn"><X className="h-4 w-4" /></Button>
                              </>
                            ) : (
                              <>
                                {viewMode === "basisliste" && (
                                  <div className="relative">
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => setQuantityPopover({ 
                                        open: quantityPopover.itemId === item.id ? !quantityPopover.open : true, 
                                        itemId: item.id, 
                                        quantity: item.antal || 1 
                                      })} 
                                      className="h-7 w-7 p-0" 
                                      title="Tilføj til kurv" 
                                      data-testid="add-to-cart-btn"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                    {/* Antal popover */}
                                    {quantityPopover.open && quantityPopover.itemId === item.id && (
                                      <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg p-2 w-40">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Input 
                                            type="number" 
                                            min="1" 
                                            value={quantityPopover.quantity} 
                                            onChange={(e) => setQuantityPopover({...quantityPopover, quantity: parseInt(e.target.value) || 1})} 
                                            className="h-7 text-sm"
                                            autoFocus
                                          />
                                          <span className="text-xs text-muted-foreground">stk</span>
                                        </div>
                                        <div className="flex gap-1">
                                          <Button 
                                            size="sm" 
                                            onClick={() => addToCart(item, quantityPopover.quantity)} 
                                            className="flex-1 h-7 text-xs"
                                          >
                                            <ShoppingCart className="h-3 w-3 mr-1" />Tilføj
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            onClick={() => setQuantityPopover({ open: false, itemId: null, quantity: 1 })} 
                                            className="h-7 w-7 p-0"
                                          >
                                            <X className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {canEditMateriel && (
                                  <Button size="sm" variant="ghost" onClick={() => setEditingItem({...item})} className="h-7 w-7 p-0" data-testid="edit-materiel-btn"><Edit2 className="h-4 w-4" /></Button>
                                )}
                                {/* Skjul-knap */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => hideItem(item.id)} 
                                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" 
                                  title="Skjul linje"
                                  data-testid="hide-item-btn"
                                >
                                  <EyeOff className="h-4 w-4" />
                                </Button>
                                {canDeleteMateriel && (
                                  <Button size="sm" variant="ghost" onClick={() => deleteItem(item.id, item.produktinformation)} className="h-7 w-7 p-0 text-destructive" data-testid="delete-materiel-btn"><Trash2 className="h-4 w-4" /></Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Skjulte linjer sektion */}
        {hiddenItems.length > 0 && viewMode === "basisliste" && (
          <div className="mt-8 border-t border-border pt-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowHiddenItems(!showHiddenItems)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <EyeOff className="h-4 w-4" />
                <span className="text-sm font-medium">Skjulte linjer ({hiddenItems.length})</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showHiddenItems ? 'rotate-180' : ''}`} />
              </button>
              {showHiddenItems && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={unhideAllItems}
                  className="text-xs"
                >
                  Vis alle
                </Button>
              )}
            </div>
            
            {showHiddenItems && (
              <div className="bg-muted/20 border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="px-3 py-2 text-left">Produkt</th>
                      <th className="px-3 py-2 text-left">Kategori</th>
                      <th className="px-3 py-2 text-left">Leverandør</th>
                      <th className="px-3 py-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {hiddenItems.map(item => (
                      <tr key={item.id} className="opacity-60 hover:opacity-100">
                        <td className="px-3 py-2">{item.produktinformation || '-'}</td>
                        <td className="px-3 py-2">{item.kategori || '-'}</td>
                        <td className="px-3 py-2">{item.leverandoer || '-'}</td>
                        <td className="px-3 py-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => unhideItem(item.id)}
                            className="h-7 px-2 text-xs"
                            title="Vis linje igen"
                          >
                            <Eye className="h-3 w-3 mr-1" />Vis
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <PoweredByFooter />
      </main>
    </div>
  );
}
